/**
 * @file countries.api.js
 * @module CountriesAPI
 * @description HTTP API wrapper for country and location lookups. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

/**
 * Namespace containing API methods related to countries and locations.
 * @namespace CountriesAPI
 */
export const CountriesAPI = {
    /**
     * Retrieve list of countries.
     *
     * @returns {Promise} Axios promise resolving to countries list.
     */
    getGlobalCountries: () => {
        return axiosInstance().get(`/catalog/countries`);
    },

    /**
     * Autocomplete locations or airports depending on type.
     *
     * @param {string} input - Input string to autocomplete.
     * @param {string} type - One of ['airport','arrival','departure'] to force airport search.
     * @param {string} [country_code=""] - Optional country code filter.
     * @returns {Promise} Axios promise resolving to matching locations.
     */
    autocompleteLocations: (input, type, country_code = "") => {
        if (["airport", "arrival", "departure"].indexOf(type) > -1) {
            return axiosInstance().get(`/airports?input=${encodeURIComponent(input)}&type=airport&country_code=${country_code}`);
        } else {
            return axiosInstance().get(`/locations?input=${encodeURIComponent(input)}&country_code=${country_code}`);
        }
    },

    /**
     * Get cities matching input.
     *
     * @param {string} input - City search input.
     * @returns {Promise} Axios promise resolving to cities list.
     */
    getCities: (input) => {
        return axiosInstance().get(`/cities?input=${encodeURIComponent(input)}`);
    },

    /**
     * Create a new country record.
     *
     * @param {Object} payload - Country data to be created (name, slug, etc.).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the created country.
     */
    createCountry: (payload, token = "") => {
        return axiosInstance(token).post(`/countries`, payload);
    },

    /**
     * Retrieve a paginated list of countries.
     *
     * @param {number} [limit=20] - Number of items per page.
     * @param {number} [page=1] - Page number to retrieve.
     * @param {string} [query=""] - Optional URL-encoded query string to filter results (e.g. 'q=foo').
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to a paginated countries response.
     */
    getCountries: async (limit = 20, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/countries?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Retrieve a single country by its ID.
     *
     * @param {string} id - Country ID.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the country object.
     */
    getCountryById: (id, token = "") => {
        return axiosInstance(token).get(`/countries/${id}`);
    },

    /**
     * Update a country by its ID.
     *
     * @param {string} id - Country ID identifying the resource to update.
     * @param {Object} payload - Partial or full country payload to update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the updated country.
     */
    updateCountryById: (id, payload, token = "") => {
        return axiosInstance(token).put(`/countries/${id}`, payload);
    },

    /**
     * Delete a country by its ID.
     *
     * @param {string} id - Country ID identifying the resource to delete.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the delete result.
     */
    deleteCountryById: (id, token = "") => {
        return axiosInstance(token).delete(`/countries/${id}`);
    },

    /**
     * Check whether a country slug is duplicated.
     *
     * @param {string} countrySlug - Slug to check for duplicates.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to an object indicating duplication status.
     */
    checkCountrySlugIsDuplicate: (countrySlug, token = "") => {
        return axiosInstance(token).get(`/countries/${countrySlug}/isDuplicate`);
    },

    /**
     * Retrieve a country by its ISO code.
     *
     * @param {string} countryCode - ISO country code.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the country object.
     */
    getCountryByCode: (countryCode, token = "") => {
        return axiosInstance(token).get(`/countries/lookup?countryCode=${countryCode}`);
    },

    /**
     * Retrieve counts of unpublished countries and locations.
     *
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to an object with unpublished counts.
     */
    getUnpublishedCounts: (token = "") => {
        return axiosInstance(token).get(`/geo/stats`);
    },

    /**
    * Change the status of a country by its ID.
    *
    * @param {string} id - Country ID.
    * @param {string} [token=""] - Optional authentication token.
    * @returns {Promise} Axios promise resolving to the updated country.
    */
    changeCountryStatusById: (id, token = "") => {
        return axiosInstance(token).patch(`/countries/${id}/status`);
    }
}